import { themeProperty } from '@risk-first/ui-style-utils';
import { osirisMoodysTheme } from '@risk-first/ui-theme';
import { rem } from 'polished';

const colors = {
  ...osirisMoodysTheme.colors,
  darkStroke: '#6C6C7B',
  lightStroke: '#E6E6EB',
  messageGreen: '#65A11B',
  textError: '#E22C2C',
  link: '#0E72C8',
  overlayBackgroundColor: 'rgba(0, 0, 0, 0.5)',
  selectionBackground: '#e6f4ff',
  primary: { 1: '#0E72C8', 2: '#3FA0EB' },
  secondary: { 1: '#2E2E43', 2: '#666788', 3: '#F7F7F8' },
  chartOrange500: '#F57C00',
  chart2: '#0097A7',
  chart: {
    1: '#3FA0EB',
    2: '#0097A7',
    3: '#9C27B0',
    4: '#FBC02D',
    5: '#512DA8',
    6: '#BCAAA4',
    7: '#C2185B',
    8: '#F57C00',
    9: '#388E3C',
    10: '#E91E63',
    11: '#4A148C',
    12: '#FF5722',
    13: '#9FA8DA',
    14: '#AFB42B',
    15: '#616161',
  },
  chart12: '#FF5722',
  earlyWarningSignalLow: '#4CAF50',
  earlyWarningSignalHigh: '#D32F2F',
  earlyWarningSignalSevere: '#C2185C',
  cyberRiskRatingAdvanced: '#0097A7',
  trend: {
    positive: themeProperty('colors.messaging.success.foreground'),
    negative: themeProperty('colors.messaging.error.foreground'),
    neutral: '#616161',
  },
  warningColor: '#FDF3BD',
};

export const theme = {
  ...osirisMoodysTheme,
  colors,
  fontFamily: `var(--font-roboto), ${osirisMoodysTheme.fontFamily}`,
  tableDataFont: `var(--font-roboto), ${osirisMoodysTheme.tableDataFont}`,
  tableHeaderFont: `var(--font-roboto), ${osirisMoodysTheme.tableHeaderFont}`,
  appHeader: {
    pv: {
      backgroundColor: themeProperty('colors.moodysBlue10'),
      color: themeProperty('colors.white'),
      brand: {
        color: themeProperty('colors.white'),
      },
      profileMenu: {
        button: {
          hover: {
            bg: 'rgba(255, 255, 255, 0.25)',
          },
          activated: {
            shadow: themeProperty('shadows.buttonDark'),
          },
          icon: {
            bg: themeProperty('colors.athensGray'),
            contentColor: themeProperty('colors.black'),
          },
        },
      },
      sidebarMenuButton: {
        color: themeProperty('colors.white'),
      },
    },
  },
  body: {
    large: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(14),
      fontWeight: 400,
      lineHeight: 16.41 / 14,
    },
    medium: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(12),
      fontWeight: 400,
      lineHeight: 14.06 / 12,
    },
    small: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(10),
      fontWeight: 400,
      lineHeight: 11.72 / 10,
    },
  },
  headline: {
    large: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(24),
      fontWeight: 400,
      lineHeight: 28.13 / 24,
    },
    medium: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(20),
      fontWeight: 400,
      lineHeight: 23.44 / 20,
    },
    small: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(18),
      fontWeight: 500,
      lineHeight: 21.09 / 18,
    },
  },
  label: {
    large: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(14),
      fontWeight: 500,
      lineHeight: 18 / 14,
    },
    medium: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(12),
      fontWeight: 500,
      lineHeight: 14.06 / 12,
    },
    small: {
      color: themeProperty('colors.secondary.1'),
      fontFamily: themeProperty('fontFamily'),
      fontSize: rem(10),
      fontWeight: 500,
      lineHeight: 11.72 / 10,
    },
  },
  secondaryInterventions: {
    ...osirisMoodysTheme.secondaryInterventions,
    error: {
      ...osirisMoodysTheme.secondaryInterventions.error,
      icon: {
        ...osirisMoodysTheme.secondaryInterventions.error.icon,
        backgroundColor: 'transparent',
        color: themeProperty('colors.errorRed'),
      },
    },
    info: {
      ...osirisMoodysTheme.secondaryInterventions.info,
      icon: {
        ...osirisMoodysTheme.secondaryInterventions.info.icon,
        backgroundColor: 'transparent',
        color: themeProperty('colors.secondary.1'),
      },
    },
    success: {
      ...osirisMoodysTheme.secondaryInterventions.success,
      icon: {
        ...osirisMoodysTheme.secondaryInterventions.success.icon,
        backgroundColor: 'transparent',
        color: themeProperty('colors.messageGreen'),
      },
    },
  },
  temporaryInterventions: {
    ...osirisMoodysTheme.temporaryInterventions,
    info: {
      ...osirisMoodysTheme.temporaryInterventions.info,
      container: {
        ...osirisMoodysTheme.temporaryInterventions.info.container,
        color: themeProperty('colors.secondary.1'),
      },
    },
  },

  /**
   * TODO: these could do with being moved to be nested under `colors`
   */
  cyberRisk: {
    rating: {
      basic: colors.textError,
      medium: colors.chartOrange500,
      advanced: colors.messageGreen,
    },
    distribution: {
      advanced: colors.messageGreen,
      medium: colors.chart[4],
      basic: colors.textError,
      unscored: colors.chart[15],
    },
    chart: {
      differenceToIndustryAverage: {
        industryAverage: colors.white,
        rating: colors.primary[1],
      },
      industryPercentile: {
        box: colors.primary[2],
        median: colors.white,
        scatter: colors.chart12,
      },
    },
    table: {
      differenceToIndustryAverage: {
        lessThanZero: colors.earlyWarningSignalHigh,
        zero: colors.chartOrange500,
        greaterThanZero: colors.earlyWarningSignalLow,
      },
      industryPercentile: {
        lessThanHalf: colors.earlyWarningSignalHigh,
        half: colors.chartOrange500,
        moreThanHalf: colors.earlyWarningSignalLow,
      },
    },
  },
  earlyWarningSignal: {
    low: colors.messageGreen,
    medium: colors.chart[4],
    high: colors.chart[8],
    severe: colors.textError,
    unscored: colors.darkStroke,
  },
  newsSentiment: {
    positive: colors.trend.positive,
    negative: colors.trend.negative,
    neutral: colors.trend.neutral,
    polarized: '#000', // temporary color for polarized
  },
  sanctionSignal: {
    alert: colors.errorRed,
    warning: colors.chartOrange500,
  },
  valuationsStatus: {
    NotStarted: colors.darkStroke,
    Failed: colors.textError,
    InProgress: colors.primary[2],
    Completed: colors.messageGreen,
  },
};
